import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAllPages } from '../utils/fetchAllPages';
import { useEstateGroups } from './useEstates';
import { useBuildings } from './useBuildings';
import { useUserTypes } from './useUserTypes';
import { useUserEstateStatuses } from './useUserEstateStatus';
import { useDeviceTokenUsers } from './useDeviceTokens';

// ─────────────────────────────────────────────────────────────────────────
// Serializable audience definition.
//
// This shape is intentionally a plain, JSON-serializable object (no React
// state, no resolved user lists) so it can be persisted as-is once SAM-265
// adds named/reusable segments — the segment would store exactly this
// object and re-resolve it against live data at send time. Keep new filter
// fields flat and serializable if you extend this.
// ─────────────────────────────────────────────────────────────────────────
export interface AudienceFilter {
  estateGroupIds: number[];
  buildingIds: number[];
  estateIds: number[];
  userTypeIds: number[];
  statusIds: number[];
  /** Manually hand-picked individual users, combined with the segment above. */
  userIds: number[];
}

export const EMPTY_AUDIENCE_FILTER: AudienceFilter = {
  estateGroupIds: [],
  buildingIds: [],
  estateIds: [],
  userTypeIds: [],
  statusIds: [],
  userIds: [],
};

export const isAudienceFilterEmpty = (f: AudienceFilter) =>
  f.estateGroupIds.length === 0 &&
  f.buildingIds.length === 0 &&
  f.estateIds.length === 0 &&
  f.userTypeIds.length === 0 &&
  f.statusIds.length === 0 &&
  f.userIds.length === 0;

// --- Lightweight flat estate list (id, name, group, building) ---
// Mirrors the pattern already used by EstatesExplorer's `useAllEstatesFlat`
// but goes through the shared client (auth/business-scoping interceptors) instead of a hand-rolled fetch.
interface FlatEstateLite {
  id: number;
  name: string;
  estate_group_id?: number | null;
  building_id?: number | null;
}

const useAllEstatesLite = () =>
  useQuery({
    queryKey: ['push-audience-estates-flat'],
    // Every page: crud caps one answer, and a group resolved from a partial estate list counts too few users.
    queryFn: () => fetchAllPages<FlatEstateLite>('/estate?order_by=name&order_direction=ASC', 1000),
    staleTime: 30_000,
  });

/**
 * The estate_group -> building -> estate hierarchy for the active business,
 * used to power the cascading selectors and to resolve a segment selection
 * down to a concrete set of estate ids.
 */
export const useEstateHierarchy = () => {
  const { allEstateGroups, isLoading: groupsLoading } = useEstateGroups({ limit: 500 });
  const { allBuildings, isLoading: buildingsLoading } = useBuildings({ limit: 1000 });
  const { data: allEstates = [], isLoading: estatesLoading } = useAllEstatesLite();

  const estateGroupOptions = useMemo(
    () => (allEstateGroups || []).map((g: any) => ({ id: g.id as number, label: g.name as string })),
    [allEstateGroups],
  );

  const buildingsByGroup = useMemo(() => {
    const map = new Map<number, Array<{ id: number; label: string }>>();
    for (const b of allBuildings as any[]) {
      const gId = b.estate_group_id ? Number(b.estate_group_id) : null;
      if (gId == null) continue;
      if (!map.has(gId)) map.set(gId, []);
      map.get(gId)!.push({ id: b.id as number, label: (b.name as string) || `Building ${b.id}` });
    }
    return map;
  }, [allBuildings]);

  return {
    isLoading: groupsLoading || buildingsLoading || estatesLoading,
    estateGroupOptions,
    allBuildings: allBuildings as any[],
    buildingsByGroup,
    allEstates: allEstates as FlatEstateLite[],
  };
};

/**
 * Resolves an AudienceFilter into a concrete list of target user ids, and
 * cross-references them against users with an active device token so the
 * caller can show the *real* reachable recipient count before sending.
 */
export const useResolveAudience = (filter: AudienceFilter) => {
  const { allEstates, isLoading: hierarchyLoading } = useEstateHierarchy();
  const { reachableUserIdSet, isLoading: devicesLoading } = useDeviceTokenUsers();

  // Cascade: most specific level chosen wins. Selecting only a higher level
  // reaches everything under it (SAM-264 acceptance criteria).
  const resolvedEstateIds = useMemo(() => {
    if (filter.estateIds.length > 0) return filter.estateIds;

    if (filter.buildingIds.length > 0) {
      const buildingSet = new Set(filter.buildingIds);
      return allEstates.filter(e => e.building_id != null && buildingSet.has(e.building_id)).map(e => e.id);
    }

    if (filter.estateGroupIds.length > 0) {
      const groupSet = new Set(filter.estateGroupIds);
      return allEstates.filter(e => e.estate_group_id != null && groupSet.has(e.estate_group_id)).map(e => e.id);
    }

    return [];
  }, [filter.estateIds, filter.buildingIds, filter.estateGroupIds, allEstates]);

  const hasEstateScope = filter.estateGroupIds.length > 0 || filter.buildingIds.length > 0 || filter.estateIds.length > 0;
  const hasRefinement = filter.userTypeIds.length > 0 || filter.statusIds.length > 0;
  // An estate scope that resolves to no estates (still loading, or an empty group) reaches nobody; it must not fall
  // through to a query without estate_id_in, which would return every user of the tenant.
  const segmentQueryEnabled = hasEstateScope ? resolvedEstateIds.length > 0 : hasRefinement;

  const segmentParams = useMemo(() => {
    const params: Record<string, string> = { expand: 'user,user_id' };
    if (resolvedEstateIds.length > 0) params.estate_id_in = resolvedEstateIds.join(',');
    if (filter.userTypeIds.length > 0) params.user_type_id_in = filter.userTypeIds.join(',');
    if (filter.statusIds.length > 0) params.status_id_in = filter.statusIds.join(',');
    return params;
  }, [resolvedEstateIds, filter.userTypeIds, filter.statusIds]);

  // crud answers the list body itself ({ data: [...], total_pages }): the HTTP client already unwrapped the axios
  // response, so the rows are read from every page of that body, never from `response.data` (an array).
  const { data: segmentRows, isLoading: segmentLoading } = useQuery({
    queryKey: ['push-audience-segment', segmentParams],
    queryFn: () => fetchAllPages<{ user_id: number }>(`/user_estate?${new URLSearchParams(segmentParams).toString()}`, 1000),
    enabled: segmentQueryEnabled,
  });

  const segmentUserIds = useMemo(() => {
    if (!segmentQueryEnabled) return [] as number[];
    const rows = segmentRows || [];
    return Array.from(new Set(rows.map(r => r.user_id).filter((id): id is number => typeof id === 'number')));
  }, [segmentRows, segmentQueryEnabled]);

  const matchedUserIds = useMemo(
    () => Array.from(new Set([...segmentUserIds, ...filter.userIds])),
    [segmentUserIds, filter.userIds],
  );

  const reachableUserIds = useMemo(
    () => matchedUserIds.filter(id => reachableUserIdSet.has(id)),
    [matchedUserIds, reachableUserIdSet],
  );

  return {
    isLoading: hierarchyLoading || devicesLoading || segmentLoading,
    resolvedEstateIds,
    matchedUserIds,
    matchedCount: matchedUserIds.length,
    reachableUserIds,
    reachableCount: reachableUserIds.length,
  };
};

export { useUserTypes, useUserEstateStatuses };
